import { useCallback, useEffect, useRef } from "react";
import { Button } from "@baluu/ui/components/button";
import { BasketConflictNotice, formatPrice } from "@baluu/ui/components/checkout";
import { passBasketLine, passValidityLabel, type PublicPass } from "@baluu/ui/lib/checkout";
import { basketModeFor, parseBasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import {
  addPurchaseToBasket,
  closePurchaseOverlay,
  pickOffers,
  proceedToBasketCheckout,
  purchaseApiBase,
} from "~/widgets/lib/purchase-overlay";
import {
  PurchaseOverlayNotice,
  PurchaseOverlayRetry,
  PurchaseOverlayShell,
  PurchaseOverlaySkeleton,
  useSchoolCheckoutConfig,
} from "../PurchaseOverlay";
import { useRequest } from "../PurchaseOverlay/useRequest";
import { useBasketConflict } from "~/widgets/lib/use-basket-conflict";
import { offersView } from "../PurchaseOverlay/views";

export type PassCheckoutOverlayProps = {
  /** The school (MongoDB id), as the other components take it. */
  businessId: string;
  /** The school's slug: its passes, and its theme. */
  businessSlug: string;
  apiUrl?: string;
  primaryColor?: string;
  /** One pass to buy; none (or one no longer on sale) shows them all. */
  passId?: string;
  /** The surface's basket (`BasketMode`): "on" or "page" offers "Add to
   *  basket" beside "Buy now". */
  basket?: string;
};

/**
 * <baluu-pass-checkout-overlay>: buy a class pass on the page, as the hosted
 * booking site's /packages page sells them. The pass (or the school's passes)
 * from `GET /widget/pages/packages/:school`; "Buy now" puts the same basket
 * line the hosted page adds (@baluu/ui `passBasketLine`) into the basket
 * checkout, which takes the buyer's details, the sign-in a school may ask
 * for, terms, a discount code and payment, and shows the confirmation.
 */
export function PassCheckoutOverlay({
  businessId,
  businessSlug,
  apiUrl,
  primaryColor = "#03c2b9",
  passId,
  basket,
}: PassCheckoutOverlayProps) {
  const school = useSchoolCheckoutConfig(businessId, apiUrl);
  const url = `${purchaseApiBase(apiUrl)}/widget/pages/packages/${encodeURIComponent(businessSlug)}`;
  const {
    data: passes,
    failed,
    retry: reload,
  } = useRequest<PublicPass[]>(url, async () => {
    const r = await fetch(url);
    if (!r.ok) return null;
    const data = await r.json();
    return Array.isArray(data?.packages) ? data.packages : [];
  });

  const close = useCallback(() => closePurchaseOverlay("pass"), []);

  const { view, canBuy } = offersView({ config: school.status, offers: passes, offersFailed: failed });

  // The basket the surface offers, once the school's settings say whether
  // it allows one (Basket Checkout off: one thing at a time everywhere).
  const mode = school.status === "ready" ? basketModeFor(parseBasketMode(basket), school.config.basket?.isEnabled) : "off";

  // A refused "Buy now" / "Add to basket", shown under the passes. Brought
  // into view on each refusal — keyed on the refusal, not the notice, which
  // is rebuilt whenever the basket changes (e.g. while the drawer is open).
  const { conflict: basketConflict, refusal, refuse } = useBasketConflict({ businessId, apiUrl, primaryColor, mode });
  const conflictRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (refusal) conflictRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [refusal]);

  const buy = (pass: PublicPass) => {
    // The basket line carries the school's name: only once it's known.
    if (school.status !== "ready") return;
    const line = passBasketLine(pass, { _id: businessId, name: school.config.name });
    const outcome = proceedToBasketCheckout({
      line,
      businessId,
      apiUrl,
      primaryColor,
      mode,
      close,
    });
    if (!outcome.ok) refuse([line]);
  };

  const addToBasket = (pass: PublicPass) => {
    if (school.status !== "ready" || mode === "off") return;
    const line = passBasketLine(pass, { _id: businessId, name: school.config.name });
    const outcome = addPurchaseToBasket({
      line,
      businessId,
      apiUrl,
      primaryColor,
      mode,
      close,
    });
    if (!outcome.ok) refuse([line]);
  };

  const shown = passes ? pickOffers(passes, passId) : [];
  const single = shown.length === 1 && Boolean(passId);

  return (
    <PurchaseOverlayShell
      title={single ? shown[0]!.name : "Class passes"}
      intro={single ? undefined : "Buy a pass now and use it to book your classes."}
      onClose={close}
      wide={!single && shown.length > 1}
    >
      {view === "config-error" ? (
        <PurchaseOverlayRetry onRetry={school.retry}>We couldn&apos;t load the shop&apos;s settings.</PurchaseOverlayRetry>
      ) : view === "offers-error" ? (
        <PurchaseOverlayRetry onRetry={reload}>We couldn&apos;t load the passes.</PurchaseOverlayRetry>
      ) : view === "loading" ? (
        <PurchaseOverlaySkeleton />
      ) : shown.length === 0 ? (
        <PurchaseOverlayNotice>There are no class passes on sale right now.</PurchaseOverlayNotice>
      ) : (
        <div className={shown.length > 1 ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-3" : "grid gap-4"}>
          {shown.map((pass) => (
            <PassOffer
              key={pass.id}
              pass={pass}
              showName={!single}
              canBuy={canBuy}
              onBuy={() => buy(pass)}
              onAddToBasket={mode === "off" ? undefined : () => addToBasket(pass)}
            />
          ))}
        </div>
      )}
      {basketConflict ? (
        <BasketConflictNotice ref={conflictRef} notice={basketConflict} className="mt-4" />
      ) : null}
    </PurchaseOverlayShell>
  );
}

function PassOffer({
  pass,
  showName,
  canBuy,
  onBuy,
  onAddToBasket,
}: {
  pass: PublicPass;
  showName: boolean;
  canBuy: boolean;
  onBuy: () => void;
  /** Offered when the surface has the basket on. */
  onAddToBasket?: () => void;
}) {
  const validity = passValidityLabel(pass);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5 text-card-foreground">
      {showName ? <h3 className="text-base font-semibold">{pass.name}</h3> : null}
      {pass.description ? <p className="text-sm text-muted-foreground">{pass.description}</p> : null}
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xl font-bold">{formatPrice(pass.price, pass.currency)}</span>
        <span className="text-sm text-muted-foreground">
          {pass.totalSessions} {pass.totalSessions === 1 ? "session" : "sessions"}
        </span>
      </div>
      {validity ? <p className="text-xs text-muted-foreground">{validity}</p> : null}
      <div className="mt-auto flex flex-col gap-2 sm:flex-row">
        <Button className="w-full sm:flex-1" onClick={onBuy} disabled={!canBuy}>
          Buy now
        </Button>
        {onAddToBasket ? (
          <Button variant="outline" className="w-full sm:flex-1" onClick={onAddToBasket} disabled={!canBuy}>
            Add to basket
          </Button>
        ) : null}
      </div>
    </div>
  );
}
